import { useEffect, useState } from 'react';
import { api, type PlatformStatus } from './api';

type PlatformFeatures = NonNullable<PlatformStatus['features']>;

/**
 * Platform feature flags from /v1/status, fetched once per SPA session and shared across pages
 * (several pages need the same flag; without the module cache each mount would re-hit the API).
 */
let cached: PlatformFeatures | null = null;
let inflight: Promise<PlatformFeatures> | null = null;

async function loadFeatures(): Promise<PlatformFeatures> {
  if (cached) return cached;
  inflight ??= api
    .platformStatus()
    .then((s) => {
      cached = s.features ?? {};
      return cached;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * True while Cyble Vision is deliberately disabled for an upgrade (features.cybleVisionUpgrading).
 * The report UI hides cyble-vision engine lines and shows the "Upgrade in progress" notice; copy
 * that advertises Cyble Vision as a live engine adapts. Defaults to false until the flags load
 * (and on fetch failure), which renders the normal enabled-state UI.
 */
export function useCybleVisionUpgrading(): boolean {
  const [upgrading, setUpgrading] = useState(() => !!cached?.cybleVisionUpgrading);
  useEffect(() => {
    let cancelled = false;
    void loadFeatures()
      .then((f) => {
        if (!cancelled) setUpgrading(!!f.cybleVisionUpgrading);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return upgrading;
}
